import React, { useEffect, useState } from 'react';
import { Check, Loader2, Save, ShieldCheck } from 'lucide-react';
import { walletApi, SavedBankDetails } from '../api';
import { SA_BANKS, OTHER_BANK, ACCOUNT_TYPES, branchCodeFor } from '../utils/saBanks';
import { apiErrorMessage } from '../utils/errors';

interface Props {
  onSaved?: (d: SavedBankDetails) => void;
}

const inputCls =
  'w-full px-3 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

const digitsOnly = (s: string) => s.replace(/\D/g, '');

const BankAccountForm: React.FC<Props> = ({ onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [bank, setBank] = useState('');
  const [customBank, setCustomBank] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [holder, setHolder] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState('cheque');

  useEffect(() => {
    walletApi
      .getSavedBankDetails()
      .then((d) => {
        if (d.has_details) {
          setHolder(d.account_holder);
          setAccountNumber(d.account_number);
          setAccountType(d.account_type || 'cheque');
          const known = SA_BANKS.find((b) => b.name === d.bank);
          if (known) {
            setBank(known.name);
            setBranchCode(known.branchCode);
          } else {
            setBank(OTHER_BANK);
            setCustomBank(d.bank);
            setBranchCode(d.branch_code);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isOther = bank === OTHER_BANK;

  const handleBankChange = (name: string) => {
    setBank(name);
    if (name === OTHER_BANK) {
      setBranchCode('');
    } else {
      setBranchCode(branchCodeFor(name) || '');
      setCustomBank('');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaved(false);

    const bankName = isOther ? customBank.trim() : bank;
    if (!bankName) return setError('Please choose your bank.');
    if (holder.trim().length < 2) return setError('Enter the account holder name.');
    if (digitsOnly(branchCode).length < 4) return setError('Enter a valid branch code.');
    if (digitsOnly(accountNumber).length < 6) return setError('Enter a valid account number.');

    setSaving(true);
    try {
      const d = await walletApi.saveBankDetails({
        account_holder: holder.trim(),
        bank: bankName,
        branch_code: digitsOnly(branchCode),
        account_number: digitsOnly(accountNumber),
        account_type: accountType,
      });
      setSaved(true);
      onSaved?.(d);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Could not save your bank details. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-3">
      <div>
        <label className={labelCls}>Bank</label>
        <select className={inputCls} value={bank} onChange={(e) => handleBankChange(e.target.value)}>
          <option value="">Select your bank…</option>
          {SA_BANKS.map((b) => (
            <option key={b.name} value={b.name}>
              {b.name}
            </option>
          ))}
          <option value={OTHER_BANK}>Other…</option>
        </select>
      </div>

      {isOther && (
        <div>
          <label className={labelCls}>Bank name</label>
          <input
            className={inputCls}
            value={customBank}
            onChange={(e) => setCustomBank(e.target.value)}
            placeholder="Your bank's name"
          />
        </div>
      )}

      <div>
        <label className={labelCls}>Branch code {!isOther && bank && <span className="text-emerald-600">· auto-filled</span>}</label>
        <input
          className={inputCls}
          value={branchCode}
          onChange={(e) => setBranchCode(e.target.value)}
          readOnly={!isOther && !!bank}
          inputMode="numeric"
          placeholder="e.g. 250655"
        />
      </div>

      <div>
        <label className={labelCls}>Account holder name</label>
        <input
          className={inputCls}
          value={holder}
          onChange={(e) => setHolder(e.target.value)}
          placeholder="Name on the account"
          autoComplete="name"
        />
      </div>

      <div>
        <label className={labelCls}>Account number</label>
        <input
          className={inputCls}
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
          placeholder="Account number"
        />
      </div>

      <div>
        <label className={labelCls}>Account type</label>
        <select className={inputCls} value={accountType} onChange={(e) => setAccountType(e.target.value)}>
          {ACCOUNT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-emerald-50 rounded-xl p-3 flex items-start space-x-2">
        <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-emerald-700">
          Your details are encrypted and only used to pay out your winnings. Double-check your account number —
          payouts go to the number you enter.
        </p>
      </div>

      {error && (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={saving}
        className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:bg-emerald-700 transition active:scale-[0.98] disabled:opacity-60"
      >
        {saving ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Saving…</span>
          </>
        ) : saved ? (
          <>
            <Check className="h-5 w-5" />
            <span>Saved</span>
          </>
        ) : (
          <>
            <Save className="h-5 w-5" />
            <span>Save bank account</span>
          </>
        )}
      </button>
    </form>
  );
};

export default BankAccountForm;
